import fetcher from '@/core/lib/fetcher'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Reseller, ResellerFormValues } from './resellers.type'
import { APIError, APIResponse } from '@/core/types/utils'
import { toast } from '../ui/use-toast'

export const resellerConfigQueryOptions = (domain: string | null) =>
  queryOptions({
    queryKey: ['resellerConfig', domain],
    queryFn: () => {
      if (!domain) return null
      return fetcher
        .headers({ 'x-domain': domain })
        .get(import.meta.env.VITE_API_BOOKING + `admin/resellers/details`)
        .json<APIResponse<Reseller>>()
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

export const resellerQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['resellers', token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + 'admin/resellers')
        .json<Promise<APIResponse<Reseller[]>>>(),
  })

export const postReseller = (reseller: ResellerFormValues, token: string) => {
  return fetcher
    .url(import.meta.env.VITE_API_BOOKING + 'admin/resellers')
    .auth(`Bearer ${token}`)
    .post(reseller)
    .json()
}

export const resellerByIdQueryOptions = (token: string, resellerId: string) =>
  queryOptions({
    queryKey: ['reseller', resellerId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `admin/resellers/${resellerId}`)
        .json<Promise<APIResponse<Reseller>>>(),
  })

export const putReseller = (
  reseller: ResellerFormValues,
  token: string,
  resellerId: string
) => {
  return fetcher
    .url(import.meta.env.VITE_API_BOOKING + `admin/resellers/${resellerId}`)
    .auth(`Bearer ${token}`)
    .put(reseller)
    .json()
}

export const useDeleteReseller = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `admin/resellers/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['resellers'],
      })
    },
    onError: (error) => {
      const errorObject: APIError<null> = JSON.parse(error.message)
      const errorMessage = errorObject.message
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}
